import { localeNumberFormat, t } from '@ctcells/i18n'

import type { ReagentBay, SupplyItem } from './supplySummary.ts'

/*
 * 소모품 요약 줄 · 서랍이 같이 쓰는 글자 — 컴포넌트 파일에서 뺀 까닭은 fast refresh 하나다.
 * 🔴 계산이 아니라 **적는 법**만 둔다. 값은 `supplySummary()` 가 정한다.
 */

const NUM = localeNumberFormat({ maximumFractionDigits: 1 })

/** µL 를 사람이 읽는 부피로 — 1 mL 이상이면 mL */
export function volumeText(ul: number): string {
  return Math.abs(ul) >= 1000 ? `${NUM().format(ul / 1000)} mL` : `${NUM().format(ul)} µL`
}

/**
 * 「남음 / 전체」 — `96/96`. 모르면 `—/96`. 시약(`reagents`)도 같은 꼴이다 — 있음인 시약 수 / 시약 수(`1/8`).
 * 🔴 시약 칩에 상태 낱말(「일부 채움」 · 글리프 `◐`) · 종 수(「8종」)를 되살리지 마라(eric 2026-09-29 「시약 8종도
 *    일부 채움 이런 거 다 빼고 11/14 이거만 남겨줘」). 뜻은 보조기술 글자(`reagentPresentText`)가 말한다.
 */
export function amountText(item: Pick<SupplyItem, 'remaining' | 'total'>): string {
  const left = item.remaining === null ? t('mask.none') : NUM().format(item.remaining)
  return `${left}/${NUM().format(item.total)}`
}

/**
 * 시약 칩의 보조기술 글자 — 「8개 중 1개 있음」. 눈에 보이는 글자는 `1/8`(있음인 시약 수 / 시약 수 · 단위 없음)이다.
 * 시약 단위 · 있음만 센다(eric 2026-10-02 「1/8개 · 미수신 7 … 이게 맞아 보이는데?」 · `supplySummary.ts` 머리말).
 * 미수신은 옆의 「미수신 N」 글자가 따로 읽힌다(이 글자에 접어 넣지 않는다).
 */
export function reagentPresentText(item: Pick<SupplyItem, 'remaining' | 'total'>): string {
  return t('monitoring.supply.reagentPresent', {
    present: NUM().format(item.remaining ?? 0),
    total: NUM().format(item.total),
  })
}

/**
 * 팁 · TC 머리 수치의 보조기술 글자 — 「9개 중 0개 남음」. 눈에 보이는 글자는 `0/9`(단위 없음 · eric 2026-10-03 「개 텍스트는
 * 빼줘」)이고 단위는 여기에만 든다.
 */
export function remainingText(item: Pick<SupplyItem, 'remaining' | 'total'>): string {
  return t('monitoring.supply.remainingOf', {
    remaining: NUM().format(item.remaining ?? 0),
    total: NUM().format(item.total),
  })
}

/** 웰 한 칸의 채움 글자 — 서랍 웰 표 */
export function wellFillText(filled: boolean): string {
  return filled
    ? `● ${t('monitoring.supply.state.filled')}`
    : `○ ${t('monitoring.supply.state.empty')}`
}

/**
 * 칩의 «값의 출처» 글자 — 팁 · TC 가 가득으로 «본» 값일 때만(장비 값 미수신 · 일부 미수신). 장비 보고 · 시약 ·
 * 불일치면 `undefined`. 🔴 칩에 글자로 세우지 않는다(eric 2026-09-28) — 툴팁 · 보조기술 이름으로만 쓴다.
 * 시료별 보기 오른쪽 판(`SupplyPanel`)의 머리 글자가 부른다(종전 요약 카드 `SupplyStrip` 은 2026-10-02 에 걷었다).
 */
export function supplyOriginText(
  item: Pick<SupplyItem, 'key' | 'remaining' | 'mismatch' | 'origin'>,
): string | undefined {
  /*
   * 🔴 시약은 출처 글자가 없다(eric 2026-10-02 「범례를 타이틀에 붙였잖아. 그러면.. 밑에.. 중복된 건 빼야지」) — 시약의 미수신 칸은
   *    가득이 아니라 빗금이라 「… 가득으로 표시」가 사실과 다르고, 머리의 「미수신 N」(보이는 글자 · 보조기술도 읽는다)이 이미 말한다.
   */
  if (item.key === 'reagents' || item.mismatch === true || item.origin === 'live') return undefined
  return t(`monitoring.supply.origin.${item.origin}`)
}

/** 칩의 툴팁 — 값 대신 「—」인 까닭(도는 작업 없음) · 합계가 선 까닭(불일치) · 가득으로 본 값 */
export function supplyHint(
  item: Pick<SupplyItem, 'key' | 'remaining' | 'mismatch' | 'origin'>,
): string | undefined {
  if (item.key === 'reagents' && item.remaining === null) return t('monitoring.supply.noRecipe')
  if (item.mismatch === true) return t('monitoring.supply.tipsMismatchNote')
  return supplyOriginText(item)
}

/** 항목 이름 — 랙 한 장이면 그 랙의 한글 이름, 시약은 「시약」 */
export function supplyName(item: Pick<SupplyItem, 'key' | 'racks'>): string {
  if (item.key === 'reagents') return t('monitoring.supply.reagents')
  return item.racks[0]?.aria() ?? item.key
}

/**
 * 시약대 소제목 — 레시피 `labware[].display_name` 먼저, 없으면 `labware_id` 에서 짓는다(매핑은 여기 한 곳).
 * `staining.reagent_bay.ambient.N` → 「상온 시약대 N」 · `…chilled` → 「냉장 시약대」(보관 온도는 소제목 옆에 따로 선다).
 * 모르는 모양은 `labware_id` 원문(지어내지 않는다) · 시약이 시약대를 안 적었으면 「시약대 미지정」.
 */
export function reagentBayName(bay: Pick<ReagentBay, 'labwareId' | 'displayName'>): string {
  if (bay.displayName !== undefined) return bay.displayName
  if (bay.labwareId === undefined) return t('monitoring.supply.bay.none')
  /* 인스턴스 접미는 점(`ambient.1` · 커밋된 레시피) · 하이픈(`ambient-1` · 정본 개명 뒤 새 레시피) 둘 다 */
  const ambient = /\.ambient[.-](\d+)$/u.exec(bay.labwareId)
  if (ambient?.[1] !== undefined) return t('monitoring.supply.bay.ambient', { index: ambient[1] })
  if (/\.chilled$/u.test(bay.labwareId)) return t('monitoring.supply.bay.chilled')
  return bay.labwareId
}
